import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Empty, Input, List, Modal, Spin, Tabs, Tag, Typography } from "antd";
import { FileTextOutlined, ProjectOutlined, ReadOutlined, SearchOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import nodeAPI from "../../../service/ApiServiceNode";
import Cookies from "universal-cookie";
import { getScreenByPath, getVisibleScreens, matchDynamicScreen, searchScreens } from "../../../navigation/screenRegistry";
import {
    enterScreenQuickNavHistory,
    getMostFrequentQuickNavScreens,
    getRecentlyClosedQuickNavScreens,
    getScreenQuickNavHistoryKey,
    heartbeatScreenQuickNavHistory,
    leaveScreenQuickNavHistory,
    pruneInactiveScreenQuickNavHistory,
    readScreenQuickNavHistory,
    SCREEN_QUICK_NAV_HEARTBEAT_MS,
    writeScreenQuickNavHistory,
} from "../../../navigation/screenQuickNavHistory";

const { Text } = Typography;

const TAB_SEARCH = "search";
const TAB_RECENTLY_CLOSED = "recently-closed";

const createQuickNavTabId = () => {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    return `screen-quick-nav-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const ScreenList = ({ screens, onSelect, emptyDescription }) => {
    if (screens.length === 0) {
        return <Empty description={emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
        <List
            dataSource={screens}
            split={false}
            renderItem={(screen) => (
                <List.Item key={screen.id} style={{ padding: 0 }}>
                    <button
                        type="button"
                        onClick={() => onSelect(screen)}
                        style={{
                            width: "100%",
                            textAlign: "left",
                            border: "1px solid #f0f0f0",
                            borderRadius: 12,
                            padding: "12px 14px",
                            background: "#fff",
                            cursor: "pointer",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                            <div style={{ minWidth: 0 }}>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Text strong>{screen.name}</Text>
                                    <Tag style={{ marginRight: 0 }}>{screen.category}</Tag>
                                </div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {screen.path}
                                </Text>
                            </div>
                        </div>
                    </button>
                </List.Item>
            )}
        />
    );
};

const ENTITY_TYPE_CONFIG = {
    project: { icon: <ProjectOutlined />, color: "blue" },
    client_invoice: { icon: <FileTextOutlined />, color: "green" },
    vendor_bill: { icon: <FileTextOutlined />, color: "orange" },
    wiki: { icon: <ReadOutlined />, color: "purple" },
};

const ENTITY_TYPE_LABELS = {
    project: "Project",
    client_invoice: "Invoice",
    vendor_bill: "Vendor Bill",
    wiki: "Wiki",
};

const EntityList = ({ entities, onSelect, loading }) => {
    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
                <Spin size="small" />
            </div>
        );
    }

    if (entities.length === 0) {
        return null;
    }

    return (
        <List
            dataSource={entities}
            split={false}
            renderItem={(entity) => {
                const config = ENTITY_TYPE_CONFIG[entity.type] || {};
                return (
                    <List.Item key={`${entity.type}-${entity.id}`} style={{ padding: 0 }}>
                        <button
                            type="button"
                            onClick={() => onSelect(entity)}
                            style={{
                                width: "100%",
                                textAlign: "left",
                                border: "1px solid #f0f0f0",
                                borderRadius: 12,
                                padding: "12px 14px",
                                background: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ fontSize: 16, color: "#999", flexShrink: 0 }}>
                                    {config.icon}
                                </span>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                        <Text strong>{entity.label}</Text>
                                        <Tag color={config.color} style={{ marginRight: 0 }}>
                                            {ENTITY_TYPE_LABELS[entity.type] || entity.type}
                                        </Tag>
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {entity.sublabel}
                                    </Text>
                                </div>
                            </div>
                        </button>
                    </List.Item>
                );
            }}
        />
    );
};

const ScreenQuickNav = ({ collapsed }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const inputRef = useRef(null);
    const cookiesRef = useRef(new Cookies());
    const tabIdRef = useRef(createQuickNavTabId());
    const activePathRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeTab, setActiveTab] = useState(TAB_SEARCH);
    const [entityResults, setEntityResults] = useState([]);
    const [entityLoading, setEntityLoading] = useState(false);
    const entityDebounceRef = useRef(null);

    const userId =
        cookiesRef.current.get("userRId") ||
        cookiesRef.current.get("userR")?.id ||
        "guest";

    const historyKey = useMemo(() => getScreenQuickNavHistoryKey(userId), [userId]);
    const visibleScreens = getVisibleScreens();
    const currentPath = `${location.pathname}${location.search}`;
    const currentScreen = useMemo(
        () => getScreenByPath(currentPath, visibleScreens),
        [currentPath, visibleScreens],
    );
    const dynamicScreen = useMemo(
        () => (currentScreen ? null : matchDynamicScreen(currentPath)),
        [currentScreen, currentPath],
    );
    const trackedPath = currentScreen?.path || dynamicScreen?.path || null;

    const [historyState, setHistoryState] = useState(() =>
        readScreenQuickNavHistory(userId),
    );

    const syncHistoryState = useCallback(
        (nextState) => {
            const savedState = writeScreenQuickNavHistory(userId, nextState);
            setHistoryState(savedState);
            return savedState;
        },
        [userId],
    );

    const refreshHistoryState = useCallback(() => {
        setHistoryState(readScreenQuickNavHistory(userId));
    }, [userId]);

    useEffect(() => {
        refreshHistoryState();
    }, [refreshHistoryState]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                setActiveTab(TAB_SEARCH);
                setOpen(true);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    // Debounced entity search
    useEffect(() => {
        if (entityDebounceRef.current) {
            clearTimeout(entityDebounceRef.current);
        }

        const trimmed = query.trim();
        if (trimmed.length < 2) {
            setEntityResults([]);
            setEntityLoading(false);
            return;
        }

        setEntityLoading(true);
        entityDebounceRef.current = setTimeout(() => {
            nodeAPI
                .get("/search/quick", { params: { q: trimmed, limit: 5 } })
                .then((response) => {
                    const results = response.data?.data?.results || response.data?.results || [];
                    setEntityResults(results);
                })
                .catch(() => {
                    setEntityResults([]);
                })
                .finally(() => {
                    setEntityLoading(false);
                });
        }, 300);

        return () => {
            if (entityDebounceRef.current) {
                clearTimeout(entityDebounceRef.current);
            }
        };
    }, [query]);

    useEffect(() => {
        const handleStorage = (event) => {
            if (event.key === historyKey) {
                refreshHistoryState();
            }
        };

        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, [historyKey, refreshHistoryState]);

    useEffect(() => {
        const now = Date.now();
        let nextState = readScreenQuickNavHistory(userId);
        const previousPath = activePathRef.current;

        if (previousPath && previousPath !== trackedPath) {
            nextState = leaveScreenQuickNavHistory(nextState, {
                path: previousPath,
                tabId: tabIdRef.current,
                now,
            });
        }

        if (trackedPath && previousPath !== trackedPath) {
            nextState = enterScreenQuickNavHistory(nextState, {
                path: trackedPath,
                tabId: tabIdRef.current,
                now,
            });
            syncHistoryState(nextState);
        } else if (trackedPath) {
            nextState = heartbeatScreenQuickNavHistory(nextState, {
                path: trackedPath,
                tabId: tabIdRef.current,
                now,
            });
            syncHistoryState(nextState);
        } else if (previousPath) {
            syncHistoryState(nextState);
        }

        activePathRef.current = trackedPath;
    }, [syncHistoryState, trackedPath, userId]);

    useEffect(() => {
        if (!trackedPath) {
            return undefined;
        }

        const heartbeat = () => {
            if (document.hidden) {
                return;
            }

            const nextState = heartbeatScreenQuickNavHistory(
                readScreenQuickNavHistory(userId),
                {
                    path: trackedPath,
                    tabId: tabIdRef.current,
                    now: Date.now(),
                },
            );
            syncHistoryState(nextState);
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                heartbeat();
            }
        };

        window.addEventListener("focus", heartbeat);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        const intervalId = window.setInterval(
            heartbeat,
            SCREEN_QUICK_NAV_HEARTBEAT_MS,
        );

        return () => {
            window.removeEventListener("focus", heartbeat);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.clearInterval(intervalId);
        };
    }, [syncHistoryState, trackedPath, userId]);

    useEffect(() => {
        const handlePageHide = () => {
            const activePath = activePathRef.current;
            if (!activePath) {
                return;
            }

            const nextState = leaveScreenQuickNavHistory(
                readScreenQuickNavHistory(userId),
                {
                    path: activePath,
                    tabId: tabIdRef.current,
                    now: Date.now(),
                },
            );
            writeScreenQuickNavHistory(userId, nextState);
        };

        window.addEventListener("pagehide", handlePageHide);
        window.addEventListener("beforeunload", handlePageHide);
        return () => {
            window.removeEventListener("pagehide", handlePageHide);
            window.removeEventListener("beforeunload", handlePageHide);
        };
    }, [userId]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const prunedState = pruneInactiveScreenQuickNavHistory(
            readScreenQuickNavHistory(userId),
        );
        syncHistoryState(prunedState);

        const timer = window.setTimeout(() => {
            if (activeTab === TAB_SEARCH) {
                inputRef.current?.focus({ cursor: "all" });
            }
        }, 50);

        return () => window.clearTimeout(timer);
    }, [activeTab, open, syncHistoryState, userId]);

    const closeModal = () => {
        setOpen(false);
        setActiveTab(TAB_SEARCH);
        setQuery("");
        setEntityResults([]);
        setEntityLoading(false);
    };

    const handleSelect = (screen) => {
        closeModal();
        if (currentPath === screen.path) {
            return;
        }
        navigate(screen.path);
    };

    const handleEntitySelect = (entity) => {
        closeModal();
        navigate(entity.path);
    };

    const handleSubmit = () => {
        if (searchResults.length > 0) {
            handleSelect(searchResults[0]);
        } else if (entityResults.length > 0) {
            handleEntitySelect(entityResults[0]);
        }
    };

    const frequentScreens = useMemo(() => {
        const rankedScreens = getMostFrequentQuickNavScreens(
            visibleScreens,
            historyState,
        );
        const hasUsageHistory = rankedScreens.some(
            (screen) => (historyState.openCountByPath?.[screen.path] || 0) > 0,
        );

        return hasUsageHistory ? rankedScreens : searchScreens("", visibleScreens);
    }, [historyState, visibleScreens]);

    const searchResults = useMemo(
        () => (query.trim() ? searchScreens(query, visibleScreens) : frequentScreens),
        [frequentScreens, query, visibleScreens],
    );

    const recentlyClosedScreens = useMemo(
        () =>
            getRecentlyClosedQuickNavScreens(visibleScreens, historyState, {
                currentPath: trackedPath,
            }),
        [historyState, trackedPath, visibleScreens],
    );

    const hasEntityQuery = query.trim().length >= 2;
    const showEntitySection = hasEntityQuery && (entityLoading || entityResults.length > 0);

    const searchTabContent = (
        <div style={{ display: "grid", gap: 12 }}>
            <Input
                ref={inputRef}
                allowClear
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onPressEnter={handleSubmit}
                prefix={<SearchOutlined />}
                placeholder='Try "vendor recognition", "INV-2025" or a project name'
            />

            {!query.trim() && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Most Frequently Opened
                </Text>
            )}

            {showEntitySection && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Screens
                </Text>
            )}

            <ScreenList
                screens={searchResults}
                onSelect={handleSelect}
                emptyDescription={query.trim() ? "No matching screens" : "No screen history yet"}
            />

            {showEntitySection && (
                <>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Projects, Invoices & Wiki
                    </Text>
                    <EntityList
                        entities={entityResults}
                        onSelect={handleEntitySelect}
                        loading={entityLoading}
                    />
                </>
            )}
        </div>
    );

    const recentlyClosedTabContent = (
        <div style={{ display: "grid", gap: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
                Screens no longer active in this browser
            </Text>

            <ScreenList
                screens={recentlyClosedScreens}
                onSelect={handleSelect}
                emptyDescription="No recently closed screens"
            />
        </div>
    );

    return (
        <>
            <div
                style={{
                    padding: collapsed ? "0 8px 12px" : "0 16px 12px",
                    borderBottom: "1px solid #f0f0f0",
                }}
            >
                <Button
                    onClick={() => {
                        setActiveTab(TAB_SEARCH);
                        setOpen(true);
                    }}
                    block={!collapsed}
                    aria-label="Go to screen"
                    style={{
                        width: collapsed ? 40 : "100%",
                        height: 40,
                        borderRadius: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: collapsed ? "center" : "space-between",
                        margin: collapsed ? "0 auto" : 0,
                    }}
                >
                    {collapsed ? (
                        <SearchOutlined />
                    ) : (
                        <span
                            style={{
                                display: "flex",
                                width: "100%",
                                minWidth: 0,
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <SearchOutlined style={{ flexShrink: 0 }} />
                            <span
                                style={{
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Go to screen
                            </span>
                            <Text
                                type="secondary"
                                style={{
                                    marginLeft: "auto",
                                    flexShrink: 0,
                                    fontSize: 12,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Ctrl/Cmd+K
                            </Text>
                        </span>
                    )}
                </Button>
            </div>

            <Modal
                open={open}
                onCancel={closeModal}
                footer={null}
                title="Go to screen"
                width={720}
                destroyOnHidden
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: TAB_SEARCH,
                            label: "Search",
                            children: searchTabContent,
                        },
                        {
                            key: TAB_RECENTLY_CLOSED,
                            label: "Recently Closed",
                            children: recentlyClosedTabContent,
                        },
                    ]}
                />
            </Modal>
        </>
    );
};

export default ScreenQuickNav;
